<template lang="pug">
app-main-content-layout(title='')
  .c-users-page
    //- Header Section
    section.c-users-page__header
      .c-users-page__header-content
        h1.c-users-page__heading Users
        p.c-users-page__subtitle Manage user accounts and permissions

    //- Navigation Tabs Section (Pill Style)
    section.c-users-page__nav-section
      .c-users-page__nav-wrapper
        .c-users-nav
          a.c-users-nav-link(
            :class="{ 'c-users-nav-link--active': currentView === 'ALL' }"
            @click.prevent="handleTabChange('ALL')"
          ) All
          a.c-users-nav-link(
            :class="{ 'c-users-nav-link--active': currentView === 'active' }"
            @click.prevent="handleTabChange('active')"
          ) Active
          a.c-users-nav-link(
            :class="{ 'c-users-nav-link--active': currentView === 'archived' }"
            @click.prevent="handleTabChange('archived')"
          ) Archived
          a.c-users-nav-link(
            :class="{ 'c-users-nav-link--active': currentView === 'invited' }"
            @click.prevent="handleTabChange('invited')"
          ) Invited

    //- Bulk Actions Section (only show when items selected)
    section.c-users-page__bulk-section(v-if="selected.length > 0")
      .c-bulk-actions-card
        .c-bulk-actions-card__content
          .c-bulk-actions-card__info
            span.c-bulk-actions-card__count {{ selected.length }} selected
          .c-bulk-actions-card__actions
            el-select.c-bulk-actions-card__select(
              v-model="setAs"
              placeholder="Set as"
              size="large"
              @change="bulkUpdate(setAs.value)"
            )
              el-option(
                v-for="item in setAsOption"
                :key="item.value"
                :label="item.label"
                :value="item"
              )

    //- Main Content Card
    section.c-users-content-card
      //- Filters
      article.c-users-content-card__filters
        UserFilters(
          v-model="searchQuery"
          :sort-value="sortBy"
          :sort-options="sortOptions"
          @search="searchUsers"
          @update:sortValue="sortBy = $event"
          @sort="sortTable"
        )

    //- Table Section
    section.c-users-table-section
      //- Skeleton Loader
      template(v-if="loading")
        .c-skeleton-loader
          .c-skeleton-row(v-for="n in 10" :key="n")
            .c-skeleton-cell.c-skeleton-cell--checkbox
            .c-skeleton-cell.c-skeleton-cell--name
            .c-skeleton-cell.c-skeleton-cell--email
            .c-skeleton-cell.c-skeleton-cell--status
      
      //- Table
      el-table(
        v-else
        :data="items.items"
        @row-click="routeToUser($event)"
        style="width: 100%"
        @selection-change="onCheckboxChange"
        ).clickable-rows
        el-table-column(width="15")
        el-table-column(type="selection" width="55")
        el-table-column(label="Name" min-width="156")
          template(#default="scope")
            .lh-sm(v-if="currentView != 'invited'")
              .font-black {{ scope.row.firstname }} {{ scope.row.lastname }}
            .lh-sm(v-else)
              .font-black {{ scope.row.name }}
        el-table-column(label="Email" min-width="180")
          template(#default="scope")
            .lh-sm
              .font-medium {{ scope.row.email }}

        el-table-column(prop="active" label="Status" min-width="60")
          template(#default="scope")
            span.status-badge.status-badge--active(v-if="scope.row.active") Active
            span.status-badge.status-badge--archived(v-else-if="scope.row.active == false") Archived
            span.status-badge.status-badge--invited(v-else-if="scope.row.entity === 'User'") Invited
      
      //- Pagination
      .c-users-pagination(v-if="currentView != 'invited'")
        el-pagination(
          layout="prev, pager, next"
          :current-page="items.paginationMetadata.currentPage"
          :total="items.paginationMetadata.totalPageCount * pageSize"
          @current-change="setPage"
        )

  dialog-add-user(
    v-if="dialog"
    v-model="dialog"
    @save-item="organizationInvite"
    :server-error="serverError"
    @close-dialog="dialog=false"
  )
</template>

<script>
import {
  defineComponent, ref, onMounted, reactive,
} from 'vue';
import VueMultiselect from 'vue-multiselect';
import UserFilters from './UserFilters.vue';
import shared from './shared';
import DialogAddUser from './DialogAddUser.vue';

export default defineComponent({
  name: 'ManageContent_Users',
  components: {
    DialogAddUser, VueMultiselect, UserFilters,
  },
  setup() {
    const {
      // route,
      router,
      loading,
      getUsers,
      createOrganizationInvite,
      doGetInvites,
      doUpdateUserIsActiveMutation,
    } = shared();
    const items = reactive({ items: [], paginationMetadata: {} });
    const invited = reactive({ items: [] });
    const serverError = ref('');
    const dialog = ref(false);
    const filter = ref('');
    const currentView = ref('ALL');
    const selected = ref([]);
    const searchQuery = ref('');
    const isFiltered = ref(false);
    const sortOptions = [{ label: 'First Name', value: 'name' }, { label: 'Last Name', value: 'last_name' }, { label: 'Email', value: 'email' }, { label: 'Recent', value: 'created_at_desc' }, { label: 'Oldest', value: 'created_at' }];
    const setAsOption = [{ label: 'Archived', value: 'archive' }];
    const setAs = ref('');
    const sortBy = ref('');
    const page = ref(1);
    const pageSize = 10;

    const setPage = (newPage) => {
      items.paginationMetadata.currentPage = newPage;
      findUsers();
    };

    function routeToUser(itm) {
      if (currentView.value !== 'invited') {
        router.push(`/manage/users/${itm.id}/edit`);
      } else {
        router.push(`/manage/invites/${itm.id}/info`);
      }
    }

    async function findUsers() {
      try {
        loading.value = true;
        const usersWithPaginationMetadata = await getUsers(searchQuery.value, sortBy.value.value, filter.value, items.paginationMetadata.currentPage, pageSize);
        items.items = usersWithPaginationMetadata.users;
        items.paginationMetadata = usersWithPaginationMetadata.paginationMetadata;
      } catch (err) {
        console.log(err);
      } finally {
        loading.value = false;
      }
    }

    async function organizationInvite(item) {
      try {
        loading.value = true;
        await createOrganizationInvite(item);
        dialog.value = false;
      } catch (err) {
        serverError.value = err.message;
      } finally {
        loading.value = false;
      }
    }

    const bullhornsTruncateSize = 4;

    function getBullhorns(bullhorns) {
      console.log(bullhorns);
      return [];
    }

    function showOnboardedStatus(item) {
      if (item.usertype === 'ORG') {
        if (item.organization) {
          return 'Onboarded';
        }
      }
      return '--';
    }

    function searchUsers() {
      if (searchQuery.value.length > 0) {
        isFiltered.value = true;
        findUsers();
      } else {
        isFiltered.value = false;
        findUsers();
      }
    }

    function sortTable() {
      if (sortBy.value.value) {
        findUsers();
      }
    }

    function onCheckboxChange(checkedItems) {
      selected.value = checkedItems;
    }

    function getClass(currentItem) {
      if (currentItem === currentView.value) {
        return 'filter-active';
      }
      return 'filter-inactive';
    }

    function getButtonClass(currentItem) {
      switch (currentItem) {
        case 'Pending':
          return 'pending-button';
        case 'Not Approved':
          return 'notapproved-button';
        default:
          return 'approved-button';
      }
    }

    function clearSearch() {
      searchQuery.value = '';
      isFiltered.value = false;
      headerFilter(currentView.value);
      findUsers();
    }

    function headerFilter(filterBy) {
      currentView.value = filterBy;
      searchQuery.value = '';
      filter.value = filterBy;
      items.paginationMetadata.currentPage = 1;
      if (filterBy === 'invited') {
        items.items = invited.items.filter((item) => item.entity === 'User');
      } else {
        findUsers();
      }
    }

    function handleTabChange(tabName) {
      headerFilter(tabName);
    }

    async function bulkUpdate(type) {
      // Check if any users are selected
      if (selected.value.length === 0) {
        // Handle case when no users are selected
        return;
      }

      try {
        // Create an array of user IDs from the selected users
        const userIds = selected.value.map((user) => user.id);

        // Determine the new `Active` status based on the `type` parameter
        const activeStatus = type !== 'archive';

        // Prepare the input objects for the GraphQL mutation
        const inputList = userIds.map((userId) => ({
          id: userId,
          active: activeStatus,
        }));

        // Define the input variable with the expected structure
        const input = inputList;

        // Make the GraphQL request using your client library
        await doUpdateUserIsActiveMutation(input);

        // Clear the selected users
        selected.value = [];

        // Fetch updated user data
        await findUsers();
      } catch (err) {
        serverError.value = err.message;
      }
    }

    async function findInvites() {
      try {
        loading.value = true;
        invited.items = await doGetInvites();
      } catch (err) {
        console.log(err);
      } finally {
        loading.value = false;
      }
    }

    onMounted(async () => {
      await findUsers();
      await findInvites();
    });

    return {
      // data (static)
      loading,
      bullhornsTruncateSize,

      // data (reactive)
      items,
      invited,
      serverError,
      input: ref(),
      dialog,
      filter,
      selected,
      searchQuery,
      isFiltered,
      currentView,
      sortOptions,
      setAsOption,
      setAs,
      sortBy,
      page,
      pageSize,
      // methods
      getBullhorns,
      showOnboardedStatus,
      organizationInvite,
      routeToUser,
      getClass,
      getButtonClass,
      headerFilter,
      onCheckboxChange,
      bulkUpdate,
      searchUsers,
      sortTable,
      clearSearch,
      findInvites,
      setPage,
      handleTabChange,
    };
  },
});
</script>

<style lang="scss" scoped>
// Color Variables - Match Dashboard.vue and Activity page
// Color Variables - Match Activity page
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

.c-users-page {
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 2rem;
    margin-bottom: 0;
  }

  &__header-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__heading {
    font-size: 1.875rem;
    font-weight: 600;
    color: $gray-900;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: $gray-600;
    margin: 0;
    line-height: 1.5;
  }

  &__nav-section {
    margin-top: 0;
    margin-bottom: 0;
  }

  &__nav-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
  }

  &__bulk-section {
    margin-top: 0;
    margin-bottom: 0;
  }
}

// Navigation Tabs (Pill Style) - Match Activity page
.c-users-nav {
  display: inline-flex;
  height: 44px;
  align-items: center;
  justify-content: center;
  background-color: $gray-100;
  border-radius: 12px;
  padding: 4px;
  gap: 0;
  flex-wrap: nowrap;
}

.c-users-nav-link {
  display: inline-flex;
  height: calc(100% - 2px);
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 10px;
  border: 1px solid transparent;
  padding: 4px 32px;
  font-size: 0.9375rem;
  font-weight: 500;
  color: $gray-700;
  white-space: nowrap;
  transition: color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: $gray-900;
  }

  &--active {
    background-color: white;
    color: $gray-900;
    font-weight: 600;
    border-color: transparent;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  }
}

// Bulk Actions Card
.c-bulk-actions-card {
  background: white;
  border: 1px solid #dfdfdf;
  border-radius: 16px;
  padding: 12px;
  box-shadow: none;
  transition: all 0.2s ease;

  &__content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.25rem;
    flex-wrap: wrap;
  }

  &__info {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }

  &__count {
    font-size: 0.875rem;
    font-weight: 600;
    color: $gray-700;
  }

  &__actions {
    display: flex;
    gap: 0.75rem;
  }

  &__select {
    width: 200px;

    :deep(.el-select__wrapper) {
      border-radius: 10px;
      border: 1.5px solid $gray-200;
      background: #F0F0F3;
      box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
      padding: 0 12px;
      height: 40px;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover {
        border-color: $color-purple-text;
        box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.08);
      }

      &.is-focused {
        border-color: $color-purple-text;
        box-shadow: 0 0 0 3px rgba(67, 73, 221, 0.12), 0 2px 4px 0 rgba(67, 73, 221, 0.08);
      }
    }
  }
}

// Main Content Card
.c-users-content-card {
  background: transparent;
  border-radius: 0;
  border: none;
  box-shadow: none;
  overflow: visible;

  &__filters {
    padding: 0;
    border-bottom: none;
  }
}

// Table Section - Match Activity page exactly
.c-users-table-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;
  overflow: hidden;
  width: 100%;
  max-width: 100%;

  // Enable horizontal scrolling
  :deep(.el-table) {
    width: 100%;
    border-radius: 0;
  }

  :deep(.el-table__body-wrapper) {
    overflow-x: auto;
  }

  // Table styling - Match Activity page
  :deep(.el-table) {
    .cell {
      color: $gray-700;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.25rem;
      white-space: nowrap;
      padding: 0.5rem 0.5rem;
    }

    th > .cell {
      text-transform: capitalize;
      color: $gray-700;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 0.05em;
      padding: 0.5rem 0.5rem;
    }

    th.el-table__cell.is-leaf {
      background-color: $gray-50;
      border-bottom: 1px solid $gray-100;
      padding: 0.5rem 0;
    }

    td.el-table__cell {
      border-bottom: 1px solid $gray-100;
      padding: 0.625rem 0;
    }

    .el-table__row {
      cursor: pointer;
      transition: background-color 0.15s ease;

      &:hover {
        background-color: $gray-50;
      }
    }

    .el-table__empty-block {
      min-height: 400px;
    }
  }
}

// Pagination - Outlined style
.c-users-pagination {
  display: flex;
  justify-content: center;
  padding: 1.5rem;
  width: 100%;
  overflow-x: auto;

  :deep(.el-pagination) {
    .el-pager {
      li {
        border-radius: 0.375rem;
        font-weight: 500;
        transition: all 0.15s ease;
        background-color: transparent;
        border: 1px solid $gray-300;
        color: $gray-700;

        &.is-active {
          background-color: transparent;
          color: $color-purple-text;
          border-color: $color-purple-text;
          font-weight: 600;
        }

        &:hover:not(.is-active) {
          background-color: transparent;
          border-color: $gray-500;
        }
      }
    }

    .btn-prev,
    .btn-next {
      border-radius: 0.375rem;
      font-weight: 500;
      transition: all 0.15s ease;
      background-color: transparent;
      border: 1px solid $gray-300;
      color: $gray-700;

      &:hover:not(:disabled) {
        background-color: transparent;
        border-color: $gray-500;
      }

      &:disabled {
        border-color: $gray-300;
        opacity: 0.5;
      }
    }
  }
}

// Skeleton Loader - Match Activity page
.c-skeleton-loader {
  width: 100%;
  padding: 1rem 0;
}

.c-skeleton-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  gap: 1rem;
  border-bottom: 1px solid $gray-100;

  &:last-child {
    border-bottom: none;
  }
}

.c-skeleton-cell {
  height: 20px;
  background: linear-gradient(90deg, #F0F0F0 25%, #E0E0E0 50%, #F0F0F0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;

  &--checkbox {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  &--name {
    width: 150px;
    flex-shrink: 0;
  }

  &--email {
    width: 200px;
    flex-shrink: 0;
  }

  &--status {
    width: 80px;
    flex-shrink: 0;
  }
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Status badges - Match Activity page badge style exactly
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
  border: 1px solid;

  &--active {
    background-color: #D1F4F0;
    color: #0D9488;
    border-color: #14B8A6;
  }

  &--archived {
    background-color: $gray-100;
    color: $gray-600;
    border-color: $gray-300;
  }

  &--invited {
    background-color: #E0E7FF;
    color: #4F46E5;
    border-color: #818CF8;
  }
}

// Font utilities
.font-black {
  font-weight: 600;
  color: $gray-900;
}

.font-medium {
  font-weight: 500;
  color: $gray-700;
}

.lh-sm {
  line-height: 1.25;
}

// Checkbox styling
:deep(.el-checkbox__label) {
  display: none;
}

// Responsive Design
@media (max-width: 768px) {
  .c-users-page {
    padding: 1.5rem;

    &__header {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }

    &__heading {
      font-size: 1.5rem;
    }

    &__subtitle {
      font-size: 0.8125rem;
    }
  }

  .c-users-nav {
    width: 100%;
    overflow-x: auto;
  }

  .c-users-nav-link {
    font-size: 0.875rem;
    padding: 4px 24px;
  }

  .c-bulk-actions-card {
    &__content {
      flex-direction: column;
      align-items: flex-start;
    }

    &__select {
      width: 100%;
    }
  }
}

@media (max-width: 480px) {
  .c-users-page {
    padding: 1rem;

    &__heading {
      font-size: 1.25rem;
    }

    &__subtitle {
      font-size: 0.75rem;
    }
  }
}
</style>